<script setup lang="ts">
import type { ContentGridDynamicCard, CardSize } from "./ContentGridDynamicTypes.ts";

defineProps<ContentGridDynamicCard & { size: CardSize }>();

const imgRef = ref(null);
const loading = ref(true);

function onImageLoad() {
	loading.value = false;
}

onMounted(() => {
	const el = imgRef.value?.$el ?? imgRef.value;
	if (!el) return;

	if (el.complete) {
		onImageLoad();
	} else {
		el.addEventListener("load", onImageLoad, { once: true });
	}
});
</script>

<template>
	<NuxtLink
		:to="link"
		class="article-container"
		:class="size"
	>
		<div class="article">
			<div class="image-wrapper">
				<TnSkeleton
					v-if="loading"
					width="100%"
					height="100%"
				/>
				<NuxtImg
					ref="imgRef"
					:class="{ loading: loading }"
					:src="image.src.split('?')[0]"
					:alt="image.alt"
					loading="lazy"
					@error="onImageLoad"
					width="582"
					quality="100"
				/>
			</div>
			<div class="content">
				<TnHeading
					type="h3"
					size="s"
					class="title"
				>
					{{ heading }}
				</TnHeading>
			</div>
		</div>
	</NuxtLink>
</template>

<style scoped lang="scss">
@use "@/assets/typography/scss/mixins" as typoMixins;

.article-container {
	--image-aspect-ratio: 3/2;
	--column-span: span 12;

	grid-column: var(--column-span);
	text-decoration: none;
	cursor: pointer;
	&:hover {
		text-decoration: underline;
		text-decoration-color: $color-neutrals-black;
	}

	.article {
		display: flex;
		flex-direction: column;
		gap: $spacing-m;
		color: $color-neutrals-black;
		.content {
			display: flex;
			flex-direction: column;
			gap: $spacing-xs;
			width: 100%;
		}
	}
	.image-wrapper {
		position: relative;
		aspect-ratio: var(--image-aspect-ratio);
		width: 100%;
		height: 100%;
		max-width: 100%;
		img {
			transition: opacity 250ms ease;
			opacity: 1;
			width: 100%;
			height: 100%;
			object-fit: cover;
			&.loading {
				opacity: 0;
			}
		}
	}

	&.xl {
		--column-span: span 12;
		--image-aspect-ratio: 3/2;
		.article {
			flex-direction: row;
			flex-wrap: nowrap;
			gap: $spacing-l;
		}
		.content {
			justify-content: center;
		}
		.title {
			@include typoMixins.font-title-xl;
		}
		@include breakpoint(mobile) {
			.article {
				flex-direction: column;
			}
			.title {
				@include typoMixins.font-title-m;
			}
		}
	}
	&.lg {
		--column-span: span 6;
		--image-aspect-ratio: 16/9;
		.title {
			@include typoMixins.font-title-m;
		}
		@include breakpoint(mobile) {
			.title {
				@include typoMixins.font-title-xs;
			}
		}
	}
	&.md {
		--column-span: span 4;
		--image-aspect-ratio: 16/9;
		.title {
			@include typoMixins.font-title-s;
		}
	}
	&.sm {
		--column-span: span 3;
		--image-aspect-ratio: 3/2;
		.title {
			@include typoMixins.font-title-xs;
		}
	}
	&.lg,
	&.md,
	&.sm {
		@include breakpoint(mobile) {
			--image-aspect-ratio: 1/1;
		}
	}

	&.md,
	&.sm {
		@include breakpoint(mobile) {
			--column-span: span 12;
			.article {
				background: $color-neutrals-25-tint;
				flex-direction: row;
				flex-wrap: nowrap;
				gap: $spacing-l;
			}
			.content {
				padding: $spacing-s $spacing-m $spacing-m 0;
			}

			.title {
				@include typoMixins.font-title-2xs;
			}
			.image-wrapper {
				width: 114px;
				min-width: 114px;
			}
		}
	}
}
</style>
